/**
 * RideChatDialog — Phase 1 ride-scoped templated messaging.
 *
 * Used by both Passenger.jsx and Driver.jsx active-ride cards. Renders:
 *   • A scrollable thread of past messages for THIS ride only.
 *   • A row of preset chips (per `selfRole`) the user can tap to send.
 *   • Toast on receive — driven by the parent (we just refresh the
 *     thread when an incoming `ride_message` WS event arrives).
 *
 * The component does not own ride state. Parent passes:
 *   • open / onOpenChange — controlled
 *   • rideId               — which thread to load
 *   • selfId, selfRole     — to colour my-message vs theirs and pick
 *                            the correct template whitelist
 *   • incomingMessage      — last WS-received message. When this prop
 *                            changes, the dialog (if open) re-fetches.
 *                            Cheap, idempotent, avoids us subscribing
 *                            twice to the realtime channel.
 *
 * Messages "expire" with the ride — the GET endpoint already returns
 * `messages: []` for completed/cancelled/missing rides, so the dialog
 * naturally empties when the parent's `activeRide` is gone.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Send, MessageSquare, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { templatesForRole } from "../lib/rideMessageTemplates";

function relTime(iso) {
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    const diffSec = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 1000));
    if (diffSec < 5)   return "just now";
    if (diffSec < 60)  return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    return dt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export function RideChatDialog({
  open,
  onOpenChange,
  rideId,
  selfId,
  selfRole,
  counterpartyName,
  incomingMessage,
}) {
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [sending,  setSending]  = useState(false);
  const threadEndRef = useRef(null);

  // Local mirror so the chips render before the first GET resolves.
  const localTemplates = useMemo(() => templatesForRole(selfRole), [selfRole]);

  const fetchThread = useCallback(async () => {
    if (!rideId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/rides/${rideId}/messages`);
      setMessages(Array.isArray(data?.messages) ? data.messages : []);
    } catch (e) {
      // 403 = not a participant (shouldn't happen — UI hides the button)
      // 4xx = ride gone / expired — fall back to empty thread.
      // eslint-disable-next-line no-console
      console.warn("[chat] fetch thread failed:", e?.message);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [rideId]);

  // Load on open + on incoming WS message (parent bumps `incomingMessage`).
  useEffect(() => {
    if (open) fetchThread();
  }, [open, fetchThread]);

  useEffect(() => {
    if (open && incomingMessage) fetchThread();
  }, [incomingMessage, open, fetchThread]);

  // Belt-and-braces refresh while the dialog is open. The WS push is the
  // primary delivery mechanism — but in dev (React StrictMode causes
  // brief reconnect gaps) and in flaky networks (mobile handovers, proxy
  // idle-kills) a WS event can occasionally be dropped between the
  // backend send and the client receive. A cheap 4-second tick guarantees
  // the open thread converges within at most one tick even under those
  // conditions. The endpoint is O(1) for typical thread sizes and the
  // poll is paused entirely whenever the dialog is closed, so steady-
  // state cost is zero.
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(fetchThread, 4000);
    return () => clearInterval(interval);
  }, [open, fetchThread]);


  // Auto-scroll to newest on update.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 50);
    return () => clearTimeout(t);
  }, [messages.length, open]);

  const send = async (templateId) => {
    if (sending || !rideId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/rides/${rideId}/messages`, { template_id: templateId });
      // Optimistic append so the sender sees their own bubble
      // immediately even if the GET race-loses the WS push.
      setMessages((prev) => {
        if (data?.id && prev.some((m) => m.id === data.id)) return prev;
        return data?.id ? [...prev, data] : prev;
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Send failed";
      toast.error(`Couldn't send: ${msg}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md p-0 gap-0 border-2 border-[#0A0A0A]"
        data-testid="ride-chat-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border bg-[#0A0A0A] text-white">
          <DialogTitle className="font-heading text-xl font-black tracking-tight flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#FFC107]" />
            QUICK MESSAGES
          </DialogTitle>
          <DialogDescription className="text-xs text-white/60">
            {counterpartyName ? `Chat with ${counterpartyName}` : "Ride-scoped messages"}
            {" · "}
            <span className="text-white/40">No phone numbers shared.</span>
          </DialogDescription>
        </DialogHeader>

        {/* Thread */}
        <div
          className="max-h-72 min-h-[120px] overflow-y-auto px-4 py-3 space-y-2 bg-[#FAFAFA]"
          data-testid="ride-chat-thread"
        >
          {loading && messages.length === 0 && (
            <div className="text-xs text-muted-foreground italic py-4 text-center">
              <Clock className="w-3 h-3 inline mr-1" /> Loading…
            </div>
          )}
          {!loading && messages.length === 0 && (
            <div
              className="text-xs text-muted-foreground py-6 text-center"
              data-testid="ride-chat-empty"
            >
              No messages yet. Tap a preset below to send.
            </div>
          )}
          {messages.map((m) => {
            const mine = m.from_user_id === selfId;
            return (
              <div
                key={m.id}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
                data-testid={`ride-chat-bubble-${mine ? "mine" : "theirs"}`}
              >
                <div
                  className={`max-w-[75%] px-3 py-2 text-sm ${
                    mine
                      ? "bg-[#FFC107] text-[#0A0A0A]"
                      : "bg-white border border-border text-foreground"
                  }`}
                >
                  <div className="font-semibold">{m.body}</div>
                  <div
                    className={`label-mini mt-1 ${
                      mine ? "text-[#0A0A0A]/60" : "text-muted-foreground"
                    }`}
                  >
                    {mine ? "You" : (m.from_name || (m.from_role === "driver" ? "Driver" : "Passenger"))}
                    {" · "}
                    {relTime(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={threadEndRef} />
        </div>

        {/* Preset chips */}
        <div className="px-4 py-3 border-t border-border bg-white space-y-2">
          <div className="label-mini text-muted-foreground">QUICK SEND</div>
          <div className="flex flex-wrap gap-2" data-testid="ride-chat-presets">
            {localTemplates.map((t) => (
              <Button
                key={t.id}
                variant="outline"
                size="sm"
                disabled={sending}
                onClick={() => send(t.id)}
                className="text-xs h-8"
                data-testid={`ride-chat-preset-${t.id}`}
              >
                <Send className="w-3 h-3 mr-1.5" /> {t.body}
              </Button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
